import { QueryClient, type QueryKey } from '@tanstack/react-query';
import { type Draft, produce } from 'immer';
import { UpdateActionType, type UpdatedEvent } from '../types';

export const queryKeyStartsWith = (prefix: readonly unknown[], queryKey: QueryKey): boolean =>
    prefix.every((segment, index) => queryKey[index] === segment);

/**
 * Safely updates query data, only applying the updater if previous data exists
 * @param queryClient - The React Query client instance
 * @param queryKey - The query key (string or string array)
 * @param updater - Function that transforms the previous data
 */
export const setQueryDataSafe = <TData>(
    queryClient: QueryClient,
    queryKey: string | QueryKey,
    updater: (data: TData) => TData
) => {
    const normalizedQueryKey = Array.isArray(queryKey) ? queryKey : [queryKey];

    // Don't bother warning if the query doesn't exist, just skip the update
    if (!queryClient.getQueryState(normalizedQueryKey)) {
        return;
    }

    queryClient.setQueryData<TData>(normalizedQueryKey, (previous) => {
        if (previous) {
            return updater(previous);
        }
        console.warn(`Query Key ${JSON.stringify(normalizedQueryKey)} does not exist`);
        return previous;
    });
};

export const handleListUpdate = <TItem extends { id: string }, TPatch extends Partial<TItem> & { id: string } = TItem>(
    queryClient: QueryClient,
    queryKey: QueryKey,
    event: UpdatedEvent<TPatch>
): void => {
    switch (event.action) {
        case UpdateActionType.Created:
            setQueryDataSafe<TItem[]>(queryClient, queryKey, (previous) =>
                produce(previous, (draft) => {
                    // Add new entities, avoiding duplicates
                    console.log('Handling Created action for entities:', event.entities);
                    event.entities.forEach((entity) => {
                        if (!draft.find((e) => e.id === entity.id)) {
                            draft.push(entity as unknown as Draft<TItem>);
                        }
                    });
                })
            );
            break;

        case UpdateActionType.Updated:
            setQueryDataSafe<TItem[]>(queryClient, queryKey, (previous) =>
                previous.map((item) => {
                    const patch = event.entities.find((e) => e.id === item.id);
                    return patch ? ({ ...item, ...patch } as TItem) : item;
                })
            );
            break;

        case UpdateActionType.Deleted:
            setQueryDataSafe<TItem[]>(queryClient, queryKey, (previous) => {
                const idsToRemove = new Set(event.entities.map((e) => e.id));
                return previous.filter((item) => !idsToRemove.has(item.id));
            });
            break;
    }
};

/**
 * Generic handler for updating entities within a collection (used for nested structures)
 * @param collection - The draft collection to update
 * @param entity - The entity to add/update/delete
 * @param action - The action type (Created, Updated, Deleted)
 * @param createDetailed - Function to create a detailed entity from a base entity
 */
export const handleEntityAction = <T extends { id: string }, TDetailed extends T>(
    collection: Draft<TDetailed[]>,
    entity: T,
    action: UpdateActionType,
    createDetailed: (entity: T) => TDetailed
): void => {
    switch (action) {
        case UpdateActionType.Created: {
            const existing = collection.find((e) => e.id === entity.id);
            if (!existing) {
                collection.push(createDetailed(entity) as Draft<TDetailed>);
            }
            break;
        }

        case UpdateActionType.Updated: {
            const index = collection.findIndex((e) => e.id === entity.id);
            if (index !== -1) {
                Object.assign(collection[index], entity);
            }
            break;
        }

        case UpdateActionType.Deleted: {
            const deleteIndex = collection.findIndex((e) => e.id === entity.id);
            if (deleteIndex !== -1) {
                collection.splice(deleteIndex, 1);
            }
            break;
        }
    }
};

export const addEntityToCollection = <T extends { id: string }, TDetailed extends T>(
    collection: Draft<TDetailed[]>,
    entity: T,
    createDetailed: (entity: T) => TDetailed
): void => {
    const existing = collection.find((e) => e.id === entity.id);
    if (!existing) {
        collection.push(createDetailed(entity) as Draft<TDetailed>);
    }
};

export const updateEntityInCollection = <T extends { id: string }, TDetailed extends T>(
    collection: Draft<TDetailed[]>,
    entity: Partial<T>
): void => {
    const index = collection.findIndex((e) => e.id === entity.id);
    if (index !== -1) {
        Object.assign(collection[index], entity);
    }
};

export const deleteEntityFromCollection = <T extends { id: string }>(collection: Draft<T[]>, id: string): void => {
    const deleteIndex = collection.findIndex((e) => e.id === id);
    if (deleteIndex !== -1) {
        collection.splice(deleteIndex, 1);
    }
};

export interface EntityId {
    id: string;
}

export const toEntityIds = (ids: string[]): EntityId[] => ids.map((id) => ({ id }));

export const toEntitiesFromIds = <TExtra extends object>(ids: string[], extra: TExtra): Array<EntityId & TExtra> =>
    ids.map((id) => ({ id, ...extra }) as EntityId & TExtra);
